import React, { useState, useEffect, useCallback } from 'react';
import { SpaceBetween, Box, Alert, Icon } from '@cloudscape-design/components';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import useUserProfile from 'src/hooks/useUserProfile';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import QuestionDetailsForm from 'src/components/question/QuestionDetailsForm';
import { useSplitPanel } from 'src/context/SplitPanelContext';

// Styles for the assistant banner component
const ASSISTANT_BANNER_STYLES = {
  container: {
    border: '2px solid #7c3aed',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#faf5ff',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  icon: {
    color: '#a855f7',
    flexShrink: 0,
  },
  content: {
    flex: 1,
  },
  header: {
    fontWeight: 'bold',
    marginBottom: '4px',
    color: '#6b21a8',
  },
  description: {
    marginBottom: '8px',
    color: '#7c3aed',
  },
  linkContainer: {
    fontWeight: 'bold',
  },
  link: {
    textDecoration: 'none',
    color: '#7c3aed',
  },
  dismissButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: '#6b7280',
    flexShrink: 0,
  },
} as const;

const AddEditQuestionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const question = location.state?.question;
  const isCopyQuestion = location.state?.copy;
  const [showAlert, setShowAlert] = useState(true);
  const [showAssistantAlert, setShowAssistantAlert] = useState(true);
  const { closeView } = useSplitPanel();
  const { getUserBusinessUnitId, getPersona, getUserId, getUserProgramId, getDivisions } =
    useUserProfile();

  const {
    checkUserPermissions,
    userPermission,
    currentUser,
    checkUserPermissionApiStatus,
  } = useUsers();

  const isEditQuestion = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.QUESTION.EDIT_QUESTION,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions]);

  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [permissionAccessConfig.QUESTION.EDIT_QUESTION],
      divisions: getDivisions(),
    });
    return () => {
      closeView();
    };
  }, [getUserBusinessUnitId(), currentUser]);

  const handleDismissAssistant = useCallback(() => {
    setShowAssistantAlert(false);
  }, []);

  const handleDismissLanguageAlert = useCallback(() => {
    setShowAlert(false);
  }, []);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  if (checkUserPermissionApiStatus.isLoading) {
    return <div></div>;
  }

  return (
    <Box margin={{ top: 's' }}>
      {isEditQuestion() ? (
        <SpaceBetween size="l">
          {showAlert && (
            <Alert
              type="info"
              header="Language support"
              dismissible
              onDismiss={handleDismissLanguageAlert}
            >
              We only support input in Gravion in English.
            </Alert>
          )}

          {showAssistantAlert && (
            <div style={ASSISTANT_BANNER_STYLES.container}>
              <div style={ASSISTANT_BANNER_STYLES.icon}>
                <Icon name="gen-ai" size="medium" />
              </div>
              <div style={ASSISTANT_BANNER_STYLES.content}>
                <div style={ASSISTANT_BANNER_STYLES.header}>
                  Gravion Audit Question Assistant
                </div>
                <div style={ASSISTANT_BANNER_STYLES.description}>
                  Use the assistant to build audit questions from scratch utilizing SOP's.
                  Powered by AI to help you create clear, effective audit questions
                  faster.
                </div>
                <div style={ASSISTANT_BANNER_STYLES.linkContainer}>
                  <a
                    href="http://us-east-1.quicksight.aws.amazon.com/sn/account/amazonbi/start/agents?view=e7b3a26c-34b1-450e-ab47-f91de391fbff"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={ASSISTANT_BANNER_STYLES.link}
                  >
                    Try the Assistant ›
                  </a>
                </div>
              </div>
              <button
                onClick={handleDismissAssistant}
                style={ASSISTANT_BANNER_STYLES.dismissButton}
                aria-label="Dismiss assistant banner"
                type="button"
              >
                <Icon name="close" size="normal" />
              </button>
            </div>
          )}

          <QuestionDetailsForm
            isCopyQuestion={isCopyQuestion}
            question={question}
            onCancel={handleCancel}
          />
        </SpaceBetween>
      ) : (
        <Alert
          type="error"
          header="Permission denied"
          dismissible
          onDismiss={handleDismissLanguageAlert}
        >
          You do not have permissions to create questions.
        </Alert>
      )}
    </Box>
  );
};

export default AddEditQuestionPage;
